import { Button, Component, Node, warn, _decorator } from 'cc';
import ValueSelector from '../../Helper/ValueSelector';
import NumberFormatter from '../Label/NumberFormatter';
import ItemGenerator from '../Misc/ItemGenerator';
import MultiThemeSource from '../MultiTheme/MultiThemeSource';

enum EventType {
    BUTTON_CLICK = 'BUTTON_CLICK',
}

const { ccclass, property } = _decorator;

@ccclass('ValueToggleSelector')
export default class ValueToggleSelector<T extends string | number = number> extends Component {
    public static readonly EVENT_TYPE = EventType;

    @property(NumberFormatter)
    private readonly valueNumber: NumberFormatter = null;

    @property(ItemGenerator)
    private readonly itemGenerator: ItemGenerator = null;

    @property
    private readonly allowSwitchOff = false;

    private _valueSelector: ValueSelector<T> = null;
    private _buttons: Button[] = [];
    private _interactable = true;
    private _onSelectedChanged: (option: Node, isSelected: boolean) => void = null;
    private _updateOptionName: (option: Node, value: T) => void = null;

    public get interactable() {
        return this._interactable;
    }
    public set interactable(value: boolean) {
        if (this._interactable === value) {
            return;
        }
        this._interactable = value;
        this._buttons.forEach((option) => {
            option.interactable = value;
        });
    }

    public onLoad() {
        const root = this.itemGenerator?.itemRoot ?? this.node;
        root.on(Node.EventType.CHILD_ADDED, this._onChildAdded, this);
        root.on(Node.EventType.CHILD_REMOVED, this._onChildRemoved, this);
        root.children.forEach((child) => this._onChildAdded(child));
    }

    public onDestroy() {
        const root = this.itemGenerator?.itemRoot ?? this.node;
        root.off(Node.EventType.CHILD_ADDED, this._onChildAdded, this);
        root.off(Node.EventType.CHILD_REMOVED, this._onChildRemoved, this);
    }

    public init(
        valueSelector: ValueSelector<T>,
        updateOptionName: (option: Node, value: T) => void,
        onSelectedChanged?: (option: Node, isSelected: boolean) => void,
    ) {
        this._valueSelector = valueSelector;
        this._onValueOptionsUpdated();
        this._onSelectedChanged = (option: Node, isSelected: boolean) => {
            const multiThemeSource = option.getComponent(MultiThemeSource);
            if (multiThemeSource) {
                multiThemeSource.theme = isSelected ? 1 : 0;
            }
            onSelectedChanged?.(option, isSelected);
        };
        this._updateOptionName = updateOptionName;
        this._buttons.forEach((option, index) => updateOptionName(option.node, this._valueSelector.options[index]));
        this._valueSelector.on(ValueSelector.EVENT_TYPE.VALUE_CHANGED, this._onValueChanged, this);
        this._valueSelector.on(ValueSelector.EVENT_TYPE.OPTIONS_UPDATED, this._onValueOptionsUpdated, this);
        this._onValueChanged();
    }

    public deinit() {
        this._valueSelector.off(ValueSelector.EVENT_TYPE.VALUE_CHANGED, this._onValueChanged, this);
        this._valueSelector.off(ValueSelector.EVENT_TYPE.OPTIONS_UPDATED, this._onValueOptionsUpdated, this);
        this._valueSelector = null;
        this._onSelectedChanged = null;
        this._updateOptionName = null;
    }

    private _onChildAdded(node: Node) {
        const button = node.getComponent(Button);
        if (!button) {
            warn(`Button not found in ${node.name}`);
            return;
        }

        this._buttons.push(button);
        button.interactable = this._interactable;
        const index = this._buttons.length - 1;
        this._updateOptionName?.(node, this._valueSelector.options[index]);
        this._onSelectedChanged?.(node, index === this._valueSelector.currentIdx);
        node.on(Button.EventType.CLICK, () => {
            this._onButtonClicked(button);
        });
    }

    private _onChildRemoved(node: Node) {
        const button = node.getComponent(Button);
        if (!button) {
            warn(`Button not found in ${node.name}`);
            return;
        }

        const index = this._buttons.indexOf(button);
        if (index < 0) {
            warn('Button is not in the list.');
            return;
        }
        this._buttons.splice(index, 1);
        node.off(Button.EventType.CLICK);
    }

    private _onButtonClicked(button: Button) {
        const index = button.node.getSiblingIndex();
        const clickOnSameButton = index === this._valueSelector.currentIdx;
        if (!clickOnSameButton || this.allowSwitchOff) {
            this._valueSelector.currentIdx = clickOnSameButton ? ValueSelector.NULL_INDEX : index;
        }
        this.node.emit(EventType.BUTTON_CLICK, button, clickOnSameButton);
    }

    private _onValueChanged() {
        this._buttons.forEach((button, index) => {
            this._onSelectedChanged(button.node, index === this._valueSelector.currentIdx);
        });
        if (this.valueNumber) {
            if (this._valueSelector.currentIdx === ValueSelector.NULL_INDEX) {
                this.valueNumber.node.active = false;
            } else {
                this.valueNumber.node.active = true;
                if (typeof this._valueSelector.value === 'number') {
                    this.valueNumber.value = this._valueSelector.value;
                }
            }
        }
    }

    private _onValueOptionsUpdated() {
        if (this.itemGenerator) {
            this.itemGenerator.displayItemCount = this._valueSelector.options.length;
        }
    }
}
